<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import {
  CheckIcon,
  CompassIcon,
  LightbulbIcon,
  LightbulbMinimalisticIcon,
} from '@solar-icons/vue/linear'
import AppButton from '../../primitives/AppButton/AppButton.vue'
import AppPopover from '../../primitives/AppPopover/AppPopover.vue'
import AppTooltip from '../../primitives/AppTooltip/AppTooltip.vue'
import { useSidebar } from '../../shell/AppSidebar/useSidebar'
import { vCoach } from '../v-coach'
import { useTourStore } from '@/stores/tour'
import type { TourSummary } from '@/stores/tour'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const tour = useTourStore()
const { state } = useSidebar()
const rail = computed(() => state.value === 'rail')

const tours = computed(() => tour.tours)
const done = computed(() => tour.completedTourCount)
const total = computed(() => tours.value.length)
const allSeen = computed(() => total.value > 0 && done.value === total.value)

const title = computed(() => t('tour.hub.title', { done: done.value, total: total.value }))

const popoverRef = ref<InstanceType<typeof AppPopover> | null>(null)

async function onStart(summary: TourSummary): Promise<void> {
  popoverRef.value?.close()
  if (summary.route && route.name !== summary.route) {
    await router.push({ name: summary.route })
  }
  tour.start(summary.id, { force: true })
}

function onToggleTips(): void {
  tour.setTipsEnabled(!tour.tipsEnabled)
}
</script>

<template>
  <AppPopover ref="popoverRef" placement="right-end" :offset="12">
    <template #trigger="{ aria }">
      <AppTooltip :content="rail ? t('tour.hub.label') : ''" placement="right">
        <template #trigger="{ aria: tipAria }">
          <button
            v-coach="'shell.tourHub'"
            type="button"
            :aria-label="rail ? t('tour.hub.label') : undefined"
            v-bind="{ ...aria, ...tipAria }"
            class="group flex h-16 w-full select-none items-center rounded-4 text-left text-caption text-text-primary outline-none hover:bg-bg-tertiary focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-blue"
            :class="rail ? 'justify-center' : 'gap-4 px-4'"
          >
            <CompassIcon class="shrink-0 text-icon-primary" aria-hidden="true" />
            <template v-if="!rail">
              <span class="min-w-0 flex-1 truncate">{{ t('tour.hub.label') }}</span>
              <span class="shrink-0 tabular-nums text-caption text-text-primary" aria-hidden="true">
                {{ done }}/{{ total }}
              </span>
            </template>
          </button>
        </template>
      </AppTooltip>
    </template>

    <div class="flex w-(--sidebar-width-mobile) max-w-full flex-col gap-12 p-12">
      <h2 class="text-body-emphasized text-text-primary">{{ title }}</h2>

      <ul class="flex flex-col gap-2">
        <li v-for="entry in tours" :key="entry.id" class="flex items-center gap-8 rounded-2 py-4">
          <span
            v-if="entry.completed"
            class="grid size-16 shrink-0 place-items-center rounded-full bg-accent-green-soft"
          >
            <CheckIcon class="text-accent-green" />
          </span>
          <span
            v-else
            class="size-16 shrink-0 rounded-full border border-border"
            aria-hidden="true"
          />

          <span
            class="min-w-0 flex-1 truncate text-body-sm-medium"
            :class="entry.completed ? 'text-text-tertiary' : 'text-text-primary'"
          >
            {{ t(entry.labelKey) }}
          </span>

          <AppButton
            :label="entry.completed ? t('tour.hub.replay') : t('tour.hub.start')"
            :variant="entry.completed ? 'ghost' : 'secondary'"
            @click="onStart(entry)"
          />
        </li>
      </ul>

      <p v-if="allSeen" class="text-caption text-text-tertiary">{{ t('tour.hub.allSeen') }}</p>

      <AppButton
        size="sm"
        variant="ghost"
        :start-icon="tour.tipsEnabled ? LightbulbMinimalisticIcon : LightbulbIcon"
        :label="tour.tipsEnabled ? t('tour.hub.turnOff') : t('tour.hub.turnOn')"
        @click="onToggleTips"
      />
    </div>
  </AppPopover>
</template>
